/**
 * investorCard.ts — primary card template for the investor feed.
 *
 * Drops a financial-metric grid + investment-score chip into the existing
 * swipe-card shell. `renderCurrentCard` in feed.ts calls `renderInvestorInfo`
 * for investor users and leaves the legacy buyer card untouched for
 * everyone else (user_type='buyer' OR user without user_type set).
 *
 * We deliberately reuse the existing `.swipe-card` photo + gesture
 * scaffolding so swipes, stamps, photo carousel, tap-for-detail, and
 * feed exits all keep working with zero duplication.
 */

import type { FeedListing, InvestorFinancingType } from '../../shared/types.js';
import type { InvestorFeedListing } from './types/api.js';
import { NA } from '../../shared/tiles.js';
import { escapeHtml, formatListingAddress } from './ui.js';
import { state } from './state.js';
import { financingLabel, resolveEffectiveLoan } from '../../shared/financing.js';
import type { EffectiveLoanTerms } from '../../shared/financing.js';
import { formatPct, formatPrice, formatSignedMoney } from '../../shared/format.js';
import {
  capRateLevel,
  cashOnCashLevel,
  dscr as dscrRatio,
  dscrLevel,
  estimatedRentFromGrm,
  grm as grmRatio,
  investmentScoreLevel,
  monthlyCashFlowFromNoi,
  monthlyPaymentForTerms,
  rentRuleBadge,
  rentToPriceRatio,
  verdictForScore,
} from '../../shared/underwriting.js';
import type { MetricLevel } from '../../shared/underwriting.js';

// ─────────────────────────────────────────────────────────────────────────
// Metric levels now come from the shared underwriting core
// (../../shared/underwriting.ts — sourced from the investment_analyzer
// brief, kept in sync with the server-side cutoffs so the UI colors match
// the deal verdict). metricColorFor maps a level to this app's CSS vars.
// scoreBadgeClass stays local: it maps investmentScoreLevel to
// card-specific CSS classes (no local cutoffs).
// ─────────────────────────────────────────────────────────────────────────

function metricColorFor(level: MetricLevel): string {
  switch (level) {
    case 'green':
      return 'var(--success)';
    case 'yellow':
      return 'var(--warning)';
    case 'red':
      return 'var(--danger)';
    default:
      return 'var(--text-muted)';
  }
}

/** The user's saved financing profile, resolved through the shared core.
 *  (shared/financing.ts is decoupled from app state — callers extract the
 *  profile fields and pass them in explicitly.) */
function userFinancingType(): InvestorFinancingType {
  const criteria = (state.user?.investment_criteria as Record<string, unknown> | undefined) || {};
  return (criteria.financing_type as InvestorFinancingType | undefined) || 'dscr';
}

function userLoanTerms(): EffectiveLoanTerms {
  const criteria = (state.user?.investment_criteria as Record<string, unknown> | undefined) || {};
  return resolveEffectiveLoan(userFinancingType(), {
    downPct: criteria.down_payment_pct as number | undefined,
    ratePct: criteria.interest_rate as number | undefined,
    termYears: criteria.loan_term_years as number | undefined,
  });
}

function scoreBadgeClass(score: number | null | undefined): string {
  switch (investmentScoreLevel(score)) {
    case 'green': return 'inv-score-green';
    case 'yellow': return 'inv-score-yellow';
    case 'red': return 'inv-score-red';
    default: return 'inv-score-unknown';
  }
}

// ─── Est. rent fallback ───
// Recovered from the listing's own GRM via the shared core
// (estimatedRentFromGrm): price / (GRM × 12) is exactly the rent the
// analyzer used. There is deliberately NO metro rent-ratio table here —
// the backend (rent_estimator.py) is the single source of truth and the
// stamped estimated_rent / migration 041 backfill cover every row the
// analyzer could score. Unscored rows show "—", never an invented figure.
// Display-only (the card is already labeled "Est. rent").

/**
 * Metric tile for DSCR/GRM. Shows the analyzer's stored figure when we
 * have it, otherwise an honestly-derived estimate (tooltip says so),
 * otherwise the shared NA ("n/a", shared/tiles.ts) — the same text the
 * detail page, Watchlist and Compare grid show; never a bare dash and
 * never an invented figure.
 */
function ratioTile(
  label: string,
  value: number | null | undefined,
  stored: number | null | undefined,
  color: string | undefined,
  estNote: string,
): string {
  const display = value == null ? NA : Number(value).toFixed(2);
  // Touch devices can't hover: derived figures get a visible "est" tag,
  // not just a tooltip, so estimates never read as analyzer output.
  const isEst = value != null && stored == null;
  const title = value == null
    ? 'Not enough data to compute'
    : isEst ? estNote : '';
  const titleAttr = title ? ` title="${title}"` : '';
  const tileColor = value == null ? 'var(--text-muted)' : (color || 'inherit');
  return `<div class="inv-metric">`
    + `<div class="inv-metric-value" style="color:${tileColor}"${titleAttr}>${display}</div>`
    + `<div class="inv-metric-label">${label}${isEst ? '<span class="inv-est">est</span>' : ''}</div></div>`;
}

/**
 * Build the investment-score badge that overlays the photo (top-right).
 * Separate from the info block so it can stack with other badges the
 * buyer card already renders (Verified, Enhanced, HomeCheck, Open house).
 */
export function renderInvestorScoreBadge(listing: FeedListing): string {
  const score = listing.investment_score ?? null;
  const cls = scoreBadgeClass(score);
  const label = verdictForScore(score);
  const display = score == null ? '—' : String(score);
  return `
    <div class="inv-score-badge ${cls}">
      <div class="inv-score-value">${escapeHtml(display)}</div>
      <div class="inv-score-label">${escapeHtml(label)}</div>
    </div>
  `;
}

/**
 * Replace the buyer card's `.swipe-card-info` block with an investor-first
 * layout: rent + key metrics grid + cash flow row. The surrounding
 * `.swipe-card` shell (photo, badges, stamps, swipe gesture) stays.
 */
export function renderInvestorInfo(listing: InvestorFeedListing): string {
  const price = formatPrice(listing.price);
  const beds = listing.beds ?? '—';
  const baths = listing.baths ?? 'N/A';
  const sqft = listing.sqft ? listing.sqft.toLocaleString() + ' sqft' : '';
  const year = listing.year_built ? String(listing.year_built) : '';
  const addr = escapeHtml(formatListingAddress(listing));

  const rent = listing.estimated_rent ?? estimatedRentFromGrm(
    typeof listing.price === 'number' ? listing.price : 0,
    listing.gross_rent_multiplier,
  );
  const cap = listing.cap_rate ?? null;
  const coc = listing.cash_on_cash_return ?? null;
  const dscrStored = listing.dscr_ratio ?? null;
  const grmStored = listing.gross_rent_multiplier ?? null;
  // Display-only fallbacks so the tiles rarely sit empty: DSCR from NOI
  // (cap_rate × price) over the user's financing-profile payment; GRM
  // straight from price ÷ annualized rent. The detail page always shows
  // the analyzer's real figures.
  const dscr = dscrStored ?? deriveDscr(listing);
  const grm = grmStored ?? deriveGrm(listing, rent);

  // Cash flow: the analyzer's own monthly figure from the feed payload
  // (the exact number the detail page shows) when present; otherwise a
  // display-only derivation from cap_rate × price − debt service, tagged
  // "est". See cardMonthlyCashFlow.
  const { value: monthlyCashFlow, estimated: cashFlowIsEst } = cardMonthlyCashFlow(listing);

  const capColor = metricColorFor(capRateLevel(cap));
  const cocColor = metricColorFor(cashOnCashLevel(coc));
  const dscrColor = metricColorFor(dscrLevel(dscr));
  const cashColor = monthlyCashFlow == null
    ? 'var(--text-muted)'
    : monthlyCashFlow >= 0
      ? 'var(--success)'
      : 'var(--danger)';

  // Path B distress chips: when a property has off-market signals
  // (pre-foreclosure / absentee / tax-delinquent / vacant / high-equity)
  // render small pills under the address so investors see the lead at
  // a glance. Order matters — most-actionable signal first.
  const distressChips: string[] = [];
  if (listing.is_pre_foreclosure) {
    distressChips.push('<span style="display:inline-flex;align-items:center;font-size:10px;font-weight:700;color:#fff;background:var(--danger);padding:2px 8px;border-radius:8px">Pre-foreclosure</span>');
  }
  if (listing.is_tax_delinquent) {
    distressChips.push('<span style="display:inline-flex;align-items:center;font-size:10px;font-weight:700;color:#fff;background:#f97316;padding:2px 8px;border-radius:8px">Tax delinquent</span>');
  }
  if (listing.is_vacant) {
    distressChips.push('<span style="display:inline-flex;align-items:center;font-size:10px;font-weight:700;color:#fff;background:var(--warning);padding:2px 8px;border-radius:8px">Vacant</span>');
  }
  if (listing.is_absentee_owner) {
    distressChips.push('<span style="display:inline-flex;align-items:center;font-size:10px;font-weight:700;color:var(--primary);background:rgba(99,102,241,0.18);border:1px solid var(--primary);padding:1px 7px;border-radius:8px">Absentee</span>');
  }
  if (listing.owner_equity_pct != null && listing.owner_equity_pct >= 50) {
    distressChips.push(`<span style="display:inline-flex;align-items:center;font-size:10px;font-weight:700;color:var(--success);background:rgba(0,212,138,0.15);border:1px solid var(--success);padding:1px 7px;border-radius:8px">${listing.owner_equity_pct.toFixed(0)}% equity</span>`);
  }
  const distressRow = distressChips.length
    ? `<div style="display:flex;gap:5px;flex-wrap:wrap;margin-top:6px">${distressChips.join('')}</div>`
    : '';

  // Honest provenance: the CoC/DSCR/score figures on this card are
  // computed under the user's saved financing profile.
  const finTerms = userLoanTerms();
  const finLabel = financingLabel(userFinancingType(), finTerms.downPct);
  // Lower-case the label mid-sentence, but keep the DSCR acronym.
  const finLabelText = finLabel.startsWith('DSCR') ? finLabel : finLabel.charAt(0).toLowerCase() + finLabel.slice(1);
  const loanNote = `Estimated — assumes ${finLabelText} at ${finTerms.ratePct}%`;
  const analyzerNote = `Computed with your saved loan terms — ${finLabelText} at ${finTerms.ratePct}%`;

  return `
    <div class="swipe-card-info inv-card-info" onclick="event.stopPropagation(); showCurrentDetail();" style="cursor:pointer">
      <div class="inv-card-price-row">
        <div class="swipe-card-price">${price}</div>
        <div class="inv-card-specs">${beds} bd · ${baths} ba${sqft ? ' · ' + sqft : ''}${year ? ' · ' + year : ''}</div>
      </div>
      <div class="swipe-card-address">${addr}</div>
      ${distressRow}
      <div class="inv-card-rent-row">
        <span class="inv-card-rent-label">Est. rent</span>
        <span class="inv-card-rent-value">${rent != null ? '$' + Math.round(rent).toLocaleString() + '/mo' : '—'}</span>
        ${renderRuleBadge(listing.price, rent)}
        <span class="inv-fin-tag" title="CoC, DSCR and score on this card are computed with your saved loan terms">${escapeHtml(finLabel)}</span>
      </div>
      <div class="inv-card-metric-grid">
        <div class="inv-metric">
          <div class="inv-metric-value" style="color:${capColor}">${cap == null ? NA : formatPct(cap, 1)}</div>
          <div class="inv-metric-label">Cap rate</div>
        </div>
        <div class="inv-metric">
          <div class="inv-metric-value" style="color:${cocColor}">${coc == null ? NA : formatPct(coc, 1)}</div>
          <div class="inv-metric-label">Cash-on-cash</div>
        </div>
        ${ratioTile('DSCR', dscr, dscrStored, dscrColor, loanNote)}
        ${ratioTile('GRM', grm, grmStored, undefined, 'Estimated from price ÷ annualized est. rent')}
      </div>
      <!-- Card-level cash flow: the analyzer's figure from the payload
           (no "est" tag — it is the deal page's number); only the
           cap-rate derivation fallback is labeled as an estimate. -->
      <div class="inv-card-cashflow" style="color:${cashColor}"${monthlyCashFlow == null ? '' : ` title="${escapeHtml(cashFlowIsEst ? loanNote : analyzerNote)}"`}>
        ${monthlyCashFlow == null ? 'Cash flow: —' : 'Cash flow: ' + formatSignedMoney(monthlyCashFlow) + '/mo' + (cashFlowIsEst ? ' <span class="inv-est">est</span>' : '')}
      </div>
    </div>
  `;
}

/**
 * 1%/2% rule badge. Rent-to-price ratio is the fastest sniff test
 * investors use for first-glance triage — below 0.5% is essentially
 * always a losing cash-flow deal; 1%+ is rare but solid; 2%+ is the
 * unicorn. We surface the ratio + verdict as a small pill next to
 * rent so the investor can pass on a bad deal without opening the
 * full analyzer.
 */
function renderRuleBadge(price: number, rent: number | null | undefined): string {
  const ratio = rentToPriceRatio(price, rent);
  if (ratio == null) return '';
  let label: string;
  let cls: string;
  switch (rentRuleBadge(ratio)) {
    case 'two-percent':
      label = '2% rule ✓';
      cls = 'inv-rule-green';
      break;
    case 'one-percent':
      label = '1% rule ✓';
      cls = 'inv-rule-green';
      break;
    case 'near-miss':
      label = `${ratio.toFixed(2)}%`;
      cls = 'inv-rule-yellow';
      break;
    default:
      label = `${ratio.toFixed(2)}%`;
      cls = 'inv-rule-red';
  }
  return `<span class="inv-rule-badge ${cls}">${label}</span>`;
}

/** Monthly cash flow for a card / Compare cell (whole dollars), and
 *  whether it is an estimate. */
export interface CardCashFlow {
  value: number | null;
  /** True when `value` came from deriveMonthlyCashFlow (render "est");
   *  false when it is the analyzer's payload figure (or null). */
  estimated: boolean;
}

/**
 * Monthly cash flow for the swipe card, map drawer and Compare grid.
 * Prefers the payload's `monthly_cash_flow` — the analyzer's figure under
 * the viewer's saved financing, identical to the detail page's "Monthly
 * cash flow" — used verbatim (rounded to whole dollars). Only when that
 * field is missing / non-finite does it fall back to deriveMonthlyCashFlow
 * (cap_rate × price − P&I), which drifts a few dollars because cap_rate
 * is rounded, so that path is flagged `estimated`.
 */
export function cardMonthlyCashFlow(
  listing: Pick<InvestorFeedListing, 'price' | 'cap_rate' | 'monthly_cash_flow'>,
): CardCashFlow {
  const payload = listing.monthly_cash_flow;
  if (typeof payload === 'number' && Number.isFinite(payload)) {
    return { value: Math.round(payload), estimated: false };
  }
  const derived = deriveMonthlyCashFlow(listing);
  return { value: derived, estimated: derived != null };
}

/**
 * Quick client-side estimate of monthly cash flow from cap_rate + price.
 * Uses the user's saved financing profile for the debt-service ballpark.
 * Returns null when we don't have enough data — the detail page always
 * shows the real number from calculate_investment_metrics().
 * Exported so the Watchlist compare grid shows the SAME figure as the
 * swipe card instead of its own 20%/6.5% re-derivation.
 */
export function deriveMonthlyCashFlow(listing: Pick<FeedListing, 'price' | 'cap_rate'>): number | null {
  const price = Number(listing.price || 0);
  const cap = Number(listing.cap_rate ?? NaN);
  if (!price || Number.isNaN(cap)) return null;
  // NOI ≈ price * cap_rate
  const annualNoi = (price * cap) / 100;
  const monthlyPI = monthlyPaymentForTerms(price, userLoanTerms());
  return monthlyCashFlowFromNoi(annualNoi, monthlyPI);
}

/**
 * Display-only cash-to-close estimate under the user's resolved loan
 * terms: down payment + the analyzer's 3% closing costs
 * (investment_analyzer.py DEFAULT_ASSUMPTIONS closing_costs_pct), rehab
 * 0 — exactly what the detail page's "Cash to close" tile shows for a
 * turnkey deal. Null when the price is missing.
 */
export function deriveCashToClose(listing: Pick<FeedListing, 'price'>): number | null {
  const price = Number(listing.price || 0);
  if (!price) return null;
  const terms = userLoanTerms();
  return Math.round((price * (terms.downPct + 3)) / 100);
}

/**
 * Display-only DSCR fallback: NOI (cap_rate × price) over the user's
 * financing-profile payment. Null when inputs are missing or there's no
 * loan (cash); the detail page shows the analyzer's real figure.
 */
function deriveDscr(listing: FeedListing): number | null {
  const price = Number(listing.price || 0);
  const cap = Number(listing.cap_rate ?? NaN);
  if (!price || Number.isNaN(cap) || cap <= 0) return null;
  const annualNoi = (price * cap) / 100;
  const annualDebt = monthlyPaymentForTerms(price, userLoanTerms()) * 12;
  return dscrRatio(annualNoi, annualDebt);
}

/**
 * Display-only GRM fallback: price ÷ annualized rent, straight arithmetic
 * on the figures already shown on the card. Null when rent is missing.
 */
function deriveGrm(listing: FeedListing, rent: number | null | undefined): number | null {
  const price = Number(listing.price || 0);
  const monthly = Number(rent || 0);
  if (!price) return null;
  return grmRatio(price, monthly * 12);
}
